import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import cropImage from './cropImage';

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9998,
  backgroundColor: 'rgba(0,0,0,0.65)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const card = {
  backgroundColor: '#fff',
  borderRadius: 10,
  overflow: 'hidden',
  width: '100%',
  maxWidth: 520,
  boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
  display: 'flex',
  flexDirection: 'column',
};

const header = {
  backgroundColor: '#1a3328',
  color: '#fff',
  padding: '0.8rem 1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const cropArea = {
  position: 'relative',
  height: 360,
  background: '#111',
};

const footer = {
  padding: '0.85rem 1.25rem',
  borderTop: '1px solid #e8f0eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  backgroundColor: '#fafcfb',
};

const sliderWrap = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
};

const btnApply = {
  backgroundColor: '#3d8c5e',
  color: '#fff',
  border: 'none',
  borderRadius: 7,
  padding: '0.38rem 1.1rem',
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const btnCancel = {
  backgroundColor: '#fdecea',
  color: '#c62828',
  border: '1.5px solid #c62828',
  borderRadius: 7,
  padding: '0.38rem 0.9rem',
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ImageCropModal = ({ imageFile, aspectRatio = 4 / 3, onComplete, onCancel, title = 'Crop Photo', stepLabel, cropOptions = {} }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (!imageFile) return;
    const url = URL.createObjectURL(imageFile);
    objectUrlRef.current = url;
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setApplying(true);
    try {
      const isPng = imageFile?.type === 'image/png';
      const blob = await cropImage(imageSrc, croppedAreaPixels, { ...cropOptions, transparent: isPng });
      const previewUrl = URL.createObjectURL(blob);
      onComplete(blob, previewUrl);
    } catch (e) {
      console.error('Crop failed', e);
      setApplying(false);
    }
  };

  if (!imageSrc) return null;

  return (
    <div style={overlay} onClick={onCancel}>
      <div style={card} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {title}{stepLabel ? <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 8, fontSize: '0.78rem' }}>{stepLabel}</span> : null}
          </span>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}>✕</button>
        </div>

        {/* Crop area */}
        <div style={cropArea}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Footer: zoom slider + buttons */}
        <div style={footer}>
          <div style={sliderWrap}>
            <span style={{ fontSize: '0.7rem', color: '#5a6e61', whiteSpace: 'nowrap' }}>Zoom</span>
            <input
              type="range"
              min={1} max={3} step={0.05}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#3d8c5e' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnCancel} onClick={onCancel}>✕ Cancel</button>
            <button style={btnApply} onClick={handleApply} disabled={applying}>
              {applying ? 'Applying…' : '✓ Apply Crop'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
